/**
 * Estado e pontuação da calculadora guiada (um bloco por tela).
 *
 * Nada aqui toca em rede, URL ou armazenamento: o estado vive só na memória do
 * componente. A regra de pontuação é a mesma da versão em formulário único —
 * um ponto por item marcado, e o total é a contagem dos itens marcados.
 */
import { MEEM_BLOCKS, MEEM_TOTAL_MAX } from './meemItems';
import { BRUCKI_2003_MEDIANS, DOMAINS_CONFIG, LOURENCO_VERAS_2006 } from './calculator';

/** Passos do modo guiado: 0 = escolaridade, 1..11 = blocos do protocolo. */
export const GUIDED_BLOCK_COUNT = MEEM_BLOCKS.length;
export const EDUCATION_STEP = 0;

export const initialState = {
  mode: 'guided', // 'guided' | 'total'
  step: EDUCATION_STEP,
  maxStep: EDUCATION_STEP, // passo mais avançado já visitado
  view: 'steps', // 'steps' | 'result'
  checked: {},
  batch: {}, // itens marcados em lote ("Marcar bloco como correto") e ainda não tocados
  totalInput: '',
  education: null, // null = ainda não respondeu; resultado trata como 'none'
  reference: 'brucki',
  ageConfirm: 'not_informed'
};

export const itemKey = (blockId, index) => `${blockId}:${index}`;

/** Total = itens marcados. Idêntico ao cálculo do formulário único. */
export function itemsTotal(checked) {
  return Object.values(checked).filter(Boolean).length;
}

/**
 * Estado de resposta de um item em `checked`: ausente = sem resposta,
 * false = errou, true = acertou. Só os `true` pontuam, então a soma é a mesma de
 * sempre; a diferença entre "sem resposta" e "errou" existe só para a interface.
 */
export function blockStatus(checked, block) {
  const answered = block.items.filter((_, i) => checked[itemKey(block.id, i)] !== undefined).length;
  if (answered === 0) return 'empty';
  return answered === block.items.length ? 'complete' : 'partial';
}

/** Blocos sem todas as respostas (puladas ou parciais). Não muda a pontuação. */
export function incompleteBlocks(checked) {
  return MEEM_BLOCKS.filter((block) => blockStatus(checked, block) !== 'complete');
}

/**
 * Regra do avanço automático: item único avança ao ser respondido; bloco com
 * várias partes avança quando a ÚLTIMA parte é respondida (incompleto -> completo).
 * Corrigir uma resposta de um bloco já completo não avança.
 */
export function shouldAutoAdvance(prevChecked, nextChecked, block) {
  if (block.items.length === 1) return nextChecked[itemKey(block.id, 0)] !== undefined;
  return blockStatus(prevChecked, block) !== 'complete' && blockStatus(nextChecked, block) === 'complete';
}

export function blockScoresFrom(checked) {
  return MEEM_BLOCKS.reduce((acc, block) => {
    acc[block.id] = block.items.filter((_, i) => checked[itemKey(block.id, i)]).length;
    return acc;
  }, {});
}

export function parseTotalInput(value) {
  return Math.min(MEEM_TOTAL_MAX, Math.max(0, parseInt(value, 10) || 0));
}

export function finalTotal(state) {
  return state.mode === 'guided' ? itemsTotal(state.checked) : parseTotalInput(state.totalInput);
}

/** Escolaridade efetiva: sem resposta, a leitura por escolaridade fica indisponível. */
export function effectiveEducation(state) {
  return state.education ?? 'none';
}

/**
 * Pontuação por bloco reagrupada nos sete domínios usados pela ficha do
 * paciente (DOMAINS_CONFIG). É o formato que o painel já entende.
 */
export function domainScoresFrom(blockScores) {
  return DOMAINS_CONFIG.reduce((acc, domain) => {
    acc[domain.id] = MEEM_BLOCKS
      .filter((block) => domainIdOf(block) === domain.id)
      .reduce((sum, block) => sum + (blockScores[block.id] || 0), 0);
    return acc;
  }, {});
}

const DOMAIN_BY_LABEL = {
  'Orientação temporal': 'temporalOrientation',
  'Orientação espacial': 'spatialOrientation',
  Registro: 'registration',
  'Atenção e cálculo': 'attentionCalculation',
  Evocação: 'recall',
  Linguagem: 'language',
  'Capacidade visuoconstrutiva': 'visuoconstruction'
};

export function domainIdOf(block) {
  return DOMAIN_BY_LABEL[block.dominio];
}

export function guidedReducer(state, action) {
  switch (action.type) {
    case 'toggle': {
      const checked = { ...state.checked, [action.key]: !state.checked[action.key] };
      return { ...state, checked, view: 'steps' };
    }
    case 'answer': {
      // value: true (acertou) | false (errou) | null (limpa: volta a "sem resposta")
      const checked = { ...state.checked };
      const batch = { ...state.batch };
      delete batch[action.key]; // tocar no item tira a marca "em lote"
      if (action.value === null) delete checked[action.key];
      else checked[action.key] = Boolean(action.value);
      return { ...state, checked, batch, view: 'steps' };
    }
    case 'allCorrect': {
      // Marca o bloco inteiro como correto e guarda quais itens ficaram "em lote".
      const block = MEEM_BLOCKS.find((b) => b.id === action.blockId);
      const checked = { ...state.checked };
      const batch = { ...state.batch };
      block.items.forEach((_, i) => { const key = itemKey(block.id, i); checked[key] = true; batch[key] = true; });
      return { ...state, checked, batch, view: 'steps' };
    }
    case 'restoreBlock': {
      // Desfazer: devolve as respostas (e marcas de lote) que o bloco tinha antes do lote.
      const block = MEEM_BLOCKS.find((b) => b.id === action.blockId);
      const checked = { ...state.checked };
      const batch = { ...state.batch };
      block.items.forEach((_, i) => {
        const key = itemKey(block.id, i);
        if (action.prev.checked[key] === undefined) delete checked[key]; else checked[key] = action.prev.checked[key];
        if (action.prev.batch[key]) batch[key] = true; else delete batch[key];
      });
      return { ...state, checked, batch, view: 'steps' };
    }
    case 'clearBlock': {
      const block = MEEM_BLOCKS.find((b) => b.id === action.blockId);
      const checked = { ...state.checked };
      const batch = { ...state.batch };
      block.items.forEach((_, i) => { delete checked[itemKey(block.id, i)]; delete batch[itemKey(block.id, i)]; });
      return { ...state, checked, batch, view: 'steps' };
    }
    case 'setBlock': {
      // Marca ou limpa todos os itens de um bloco de uma vez.
      const block = MEEM_BLOCKS.find((b) => b.id === action.blockId);
      const checked = { ...state.checked };
      block.items.forEach((_, i) => { checked[itemKey(block.id, i)] = Boolean(action.value); });
      return { ...state, checked, view: 'steps' };
    }
    case 'setMode':
      return { ...state, mode: action.mode, view: 'steps', step: EDUCATION_STEP };
    case 'setEducation':
      return { ...state, education: action.value };
    case 'setReference':
      return { ...state, reference: action.value };
    case 'setAgeConfirm':
      return { ...state, ageConfirm: action.value };
    case 'setTotal':
      return { ...state, totalInput: action.value, view: 'steps' };
    case 'goto': {
      const step = Math.max(EDUCATION_STEP, Math.min(GUIDED_BLOCK_COUNT, action.step));
      return { ...state, step, maxStep: Math.max(state.maxStep, step), view: 'steps' };
    }
    case 'showResult':
      return { ...state, view: 'result' };
    case 'reset':
      return { ...initialState, reference: state.reference };
    default:
      return state;
  }
}

/**
 * Estado de uma célula da régua: 'hit' (acertou), 'miss' (errou) ou 'na' (não avaliado).
 * Só 'hit' pontua; a soma dos 'hit' é o total, igual ao cálculo de sempre.
 */
export function cellState(checked, key) {
  const v = checked[key];
  return v === true ? 'hit' : v === false ? 'miss' : 'na';
}

/** Itens não avaliados (sem resposta) em todos os blocos. */
export function unevaluatedCount(checked) {
  return MEEM_BLOCKS.reduce((n, b) => n + b.items.filter((_, i) => checked[itemKey(b.id, i)] === undefined).length, 0);
}

/**
 * Ponto de referência a marcar na régua do resultado, com os valores que o código já tem:
 * mediana do grupo (Brucki 2003) ou divisão de rastreio (Lourenço e Veras 2006, só com >65 anos confirmado).
 * A mediana NÃO é ponto de corte diagnóstico (a cópia clínica diz isso), então o rótulo é "mediana do grupo".
 */
export function referenceMark({ education, reference, ageConfirm }) {
  if (reference === 'brucki') {
    const value = BRUCKI_2003_MEDIANS[education];
    return value === undefined ? null : { value, kind: 'mediana', label: `mediana do grupo: ${String(value).replace('.', ',')}` };
  }
  if (reference === 'lourenco' && ageConfirm === 'yes') {
    const cfg = education === 'noFormalEducation' ? LOURENCO_VERAS_2006.noFormalEducation : LOURENCO_VERAS_2006.anyFormalEducation;
    return { value: cfg.upperSideMinimum, kind: 'divisao', label: `divisão de rastreio: ${cfg.upperSideMinimum}` };
  }
  return null;
}

/**
 * Grupos da Régua de 30 = os domínios REAIS da calculadora (DOMAINS_CONFIG), na ordem do protocolo:
 * orientação temporal 5, orientação espacial 5, registro 3, atenção e cálculo 5, evocação 3,
 * linguagem 8 e capacidade visuoconstrutiva 1 = 30. `blocks` são os blocos da página de cada domínio.
 */
const DOMAIN_SHORT = {
  temporalOrientation: { abbr: 'Temp', copy: 'Orient temp' },
  spatialOrientation: { abbr: 'Esp', copy: 'Orient esp' },
  registration: { abbr: 'Mem', copy: 'Mem' },
  attentionCalculation: { abbr: 'Aten', copy: 'Aten' },
  recall: { abbr: 'Evoc', copy: 'Evoc' },
  language: { abbr: 'Ling', copy: 'Ling' },
  visuoconstruction: { abbr: 'Des', copy: 'Des' },
};
export const RULER_GROUPS = DOMAINS_CONFIG.map((domain) => {
  const blocks = MEEM_BLOCKS.filter((b) => domainIdOf(b) === domain.id);
  return { id: domain.id, label: domain.label, abbr: DOMAIN_SHORT[domain.id].abbr, copy: DOMAIN_SHORT[domain.id].copy, blocks, max: blocks.reduce((n, b) => n + b.items.length, 0) };
});

/** Parcial de um grupo: { hits, max }. */
export function groupPartial(checked, group) {
  const hits = group.blocks.reduce((n, b) => n + b.items.filter((_, i) => checked[itemKey(b.id, i)] === true).length, 0);
  return { hits, max: group.max };
}

/** Fração (0–1) dos itens do teste que foram marcados em lote e não tocados depois. */
export function batchShare(batch) {
  return Object.values(batch).filter(Boolean).length / 30;
}

/** Chave do primeiro item sem resposta (para o link "Ir ao primeiro"), ou null. */
export function firstUnevaluated(checked) {
  for (const b of MEEM_BLOCKS) for (let i = 0; i < b.items.length; i += 1) if (checked[itemKey(b.id, i)] === undefined) return { blockId: b.id, index: i };
  return null;
}
